import type {
  ComplianceCheck,
  Country,
  Escrow,
  Invoice,
  PaymentMethod,
  PaymentRequest,
  PayrollRun,
  Recipient,
  ScanResult,
  SecurityDevice,
  SecurityEvent,
  Subscription,
  Transaction,
  TransactionLimit,
  Wallet,
} from '../types';

const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();
const hoursAgo = (n: number) => new Date(Date.now() - n * 3_600_000).toISOString();
const daysAhead = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString();

export const MOCK_WALLETS: Wallet[] = [
  { id: 'w-usd', label: 'Main Wallet', currency: 'USD', balance: 12500.75, type: 'fiat', primary: true, changePct: 2.5 },
  { id: 'w-eur', label: 'Euro Wallet', currency: 'EUR', balance: 3820.4, type: 'fiat', changePct: -0.8 },
  { id: 'w-gbp', label: 'Pound Wallet', currency: 'GBP', balance: 1540.0, type: 'fiat', changePct: 1.1 },
  { id: 'w-ngn', label: 'Naira Wallet', currency: 'NGN', balance: 2_450_000, type: 'fiat', changePct: 0.4 },
  { id: 'w-btc', label: 'Bitcoin', currency: 'BTC', balance: 0.452, type: 'crypto', address: 'bc1qxy2k...9s8h', changePct: 4.2 },
  { id: 'w-eth', label: 'Ethereum', currency: 'ETH', balance: 5.21, type: 'crypto', address: '0x71C7...976F', changePct: -2.1 },
  { id: 'w-usdc', label: 'USD Coin', currency: 'USDC', balance: 8200, type: 'crypto', address: '0x4f3e...11ad', changePct: 0.0 },
];

export const MOCK_RECIPIENTS: Recipient[] = [
  { id: 'r-1', type: 'user', name: 'John Doe', handle: 'john.doe@example.com', avatarColor: 'bg-blue-500', verified: true, country: 'US' },
  { id: 'r-2', type: 'user', name: 'Amara Okafor', handle: '+234 803 555 0192', avatarColor: 'bg-emerald-500', verified: true, country: 'NG' },
  { id: 'r-3', type: 'company', name: 'Tech Corp Ltd', handle: 'billing@techcorp.com', avatarColor: 'bg-purple-500', verified: true, country: 'GB' },
  { id: 'r-4', type: 'career', name: 'Sarah Mensah', handle: 'sarah.mensah@peskedo.pro', avatarColor: 'bg-orange-500', verified: true, role: 'Senior UX Consultant', country: 'CA' },
  { id: 'r-5', type: 'career', name: 'David Kim', handle: 'david.kim@peskedo.pro', avatarColor: 'bg-rose-500', verified: false, role: 'Freelance Developer', country: 'KR' },
  { id: 'r-6', type: 'external', name: 'Wells Fargo •••• 8842', handle: 'ACH / Routing 121000248', avatarColor: 'bg-slate-500', verified: true, country: 'US' },
  { id: 'r-7', type: 'company', name: 'Globex Industries', handle: 'ap@globex.io', avatarColor: 'bg-indigo-500', verified: true, country: 'DE' },
];

export const MOCK_PAYMENT_METHODS: PaymentMethod[] = [
  { id: 'pm-bank', type: 'bank', label: 'Bank Transfer', detail: 'Chase •••• 4821', feePct: 0, etaMinutes: 1440, default: true },
  { id: 'pm-card', type: 'card', label: 'Debit / Credit Card', detail: 'Visa •••• 4290', feePct: 1.5, etaMinutes: 1 },
  { id: 'pm-wallet', type: 'wallet', label: 'Peskedo Wallet', detail: 'USD Balance', feePct: 0, etaMinutes: 0 },
  { id: 'pm-crypto', type: 'crypto', label: 'Crypto', detail: 'BTC / ETH / USDC', feePct: 0.5, etaMinutes: 30 },
  { id: 'pm-qr', type: 'qr', label: 'QR Code', detail: 'Scan to pay', feePct: 0, etaMinutes: 1 },
];

const COUNTERPARTIES = ['John Doe', 'Tech Corp Ltd', 'Sarah Mensah', 'Amara Okafor', 'Globex Industries', 'Coffee Shop', 'AWS', 'Netflix'];

export const MOCK_TRANSACTIONS: Transaction[] = Array.from({ length: 28 }).map((_, i) => {
  const direction = i % 3 === 0 ? 'received' : 'sent';
  const categories = ['transfer', 'service', 'subscription', 'payroll', 'international', 'escrow', 'debt'] as const;
  const statuses = ['completed', 'completed', 'completed', 'pending', 'processing', 'failed', 'disputed', 'refunded'] as const;
  const amount = Math.round((Math.random() * 2400 + 25) * 100) / 100;
  return {
    id: `tx-${1000 + i}`,
    reference: `PSK-${(928374 - i * 13).toString(36).toUpperCase()}`,
    direction,
    category: categories[i % categories.length],
    amount,
    currency: i % 5 === 0 ? 'EUR' : 'USD',
    fee: Math.round(amount * 0.012 * 100) / 100,
    counterparty: COUNTERPARTIES[i % COUNTERPARTIES.length],
    counterpartyHandle: 'account@peskedo.com',
    method: (['bank', 'card', 'wallet', 'crypto', 'qr'] as const)[i % 5],
    status: statuses[i % statuses.length],
    note: i % 4 === 0 ? 'Monthly invoice settlement' : undefined,
    date: daysAgo(i),
  };
});

export const MOCK_COUNTRIES: Country[] = [
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', supported: true, avgEtaHours: 1, swift: true },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', supported: true, avgEtaHours: 2, swift: true },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', currency: 'NGN', supported: true, avgEtaHours: 24, swift: true },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', supported: true, avgEtaHours: 4, swift: true },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD', supported: true, avgEtaHours: 3, swift: true },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD', supported: true, avgEtaHours: 12, swift: true },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY', supported: true, avgEtaHours: 8, swift: true },
  { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR', supported: true, avgEtaHours: 24, swift: true },
  { code: 'CN', name: 'China', flag: '🇨🇳', currency: 'CNY', supported: false, avgEtaHours: 48, swift: false },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', currency: 'CHF', supported: true, avgEtaHours: 6, swift: true },
];

export const MOCK_COMPLIANCE: ComplianceCheck[] = [
  { id: 'c-1', label: 'Identity Verification (KYC)', status: 'passed', description: 'Government ID verified and approved.' },
  { id: 'c-2', label: 'Sanctions / OFAC Screening', status: 'passed', description: 'No matches found against watchlists.' },
  { id: 'c-3', label: 'Source of Funds', status: 'pending', description: 'Awaiting document review for amounts over $10,000.' },
  { id: 'c-4', label: 'Recipient Bank Validation', status: 'passed', description: 'SWIFT/BIC validated successfully.' },
  { id: 'c-5', label: 'Tax Reporting Declaration', status: 'action-required', description: 'Complete W-8BEN for cross-border transfers.' },
];

export const MOCK_ESCROWS: Escrow[] = [
  {
    id: 'esc-1',
    title: 'Website Redesign Project',
    counterparty: 'Sarah Mensah',
    amount: 8500,
    currency: 'USD',
    status: 'in-progress',
    createdAt: daysAgo(12),
    releaseConditions: ['Final designs delivered', 'Client approval received', '14-day review window'],
    milestones: [
      { id: 'm-1', title: 'Discovery & Wireframes', amount: 2500, status: 'released', dueDate: daysAgo(8) },
      { id: 'm-2', title: 'High-fidelity Designs', amount: 3500, status: 'funded', dueDate: daysAhead(3) },
      { id: 'm-3', title: 'Handoff & Documentation', amount: 2500, status: 'pending', dueDate: daysAhead(14) },
    ],
  },
  {
    id: 'esc-2',
    title: 'Mobile App Development',
    counterparty: 'David Kim',
    amount: 15000,
    currency: 'USD',
    status: 'in-review',
    createdAt: daysAgo(30),
    releaseConditions: ['App published to stores', 'QA sign-off'],
    milestones: [
      { id: 'm-4', title: 'MVP Build', amount: 9000, status: 'released' },
      { id: 'm-5', title: 'Store Launch', amount: 6000, status: 'funded' },
    ],
  },
  {
    id: 'esc-3',
    title: 'Equipment Purchase',
    counterparty: 'Globex Industries',
    amount: 4200,
    currency: 'EUR',
    status: 'funded',
    createdAt: daysAgo(2),
    releaseConditions: ['Goods received and inspected'],
    milestones: [{ id: 'm-6', title: 'Full delivery', amount: 4200, status: 'funded' }],
  },
];

export const MOCK_INVOICES: Invoice[] = [
  { id: 'inv-1', number: 'INV-2041', client: 'Tech Corp Ltd', amount: 3200, currency: 'USD', status: 'paid', issuedAt: daysAgo(20), dueAt: daysAgo(5) },
  { id: 'inv-2', number: 'INV-2042', client: 'Globex Industries', amount: 5400, currency: 'EUR', status: 'sent', issuedAt: daysAgo(8), dueAt: daysAhead(7) },
  { id: 'inv-3', number: 'INV-2043', client: 'Amara Okafor', amount: 850, currency: 'USD', status: 'overdue', issuedAt: daysAgo(40), dueAt: daysAgo(10) },
  { id: 'inv-4', number: 'INV-2044', client: 'John Doe', amount: 1200, currency: 'USD', status: 'draft', issuedAt: daysAgo(1), dueAt: daysAhead(14) },
];

export const MOCK_REQUESTS: PaymentRequest[] = [
  { id: 'req-1', from: 'Amara Okafor', amount: 120, currency: 'USD', reason: 'Lunch + transport', status: 'pending', createdAt: hoursAgo(3) },
  { id: 'req-2', from: 'Tech Corp Ltd', amount: 2400, currency: 'USD', reason: 'Consulting retainer', status: 'pending', createdAt: daysAgo(1) },
  { id: 'req-3', from: 'David Kim', amount: 600, currency: 'USD', reason: 'Bug fix sprint', status: 'paid', createdAt: daysAgo(4) },
];

export const MOCK_SCANS: ScanResult[] = [
  { id: 's-1', type: 'qr', payload: 'peskedo://pay/merchant/blue-bottle', merchant: 'Blue Bottle Coffee', amount: 8.5, currency: 'USD', scannedAt: hoursAgo(2) },
  { id: 's-2', type: 'qr', payload: 'peskedo://pay/user/r-2', merchant: 'Amara Okafor', amount: 50, currency: 'USD', scannedAt: daysAgo(1) },
  { id: 's-3', type: 'barcode', payload: '0123456789012', merchant: 'Whole Foods Market', amount: 124.3, currency: 'USD', scannedAt: daysAgo(2) },
];

export const MOCK_DEVICES: SecurityDevice[] = [
  { id: 'd-1', name: 'MacBook Pro', os: 'macOS 15.3', location: 'New York, US', lastActive: 'now', trusted: true, current: true },
  { id: 'd-2', name: 'iPhone 16 Pro', os: 'iOS 18.2', location: 'New York, US', lastActive: hoursAgo(5), trusted: true, current: false },
  { id: 'd-3', name: 'Windows PC', os: 'Windows 11', location: 'Lagos, NG', lastActive: daysAgo(6), trusted: false, current: false },
];

export const MOCK_SECURITY_EVENTS: SecurityEvent[] = [
  { id: 'se-1', type: 'login', description: 'Successful login from MacBook Pro', at: hoursAgo(1), status: 'ok' },
  { id: 'se-2', type: 'payment', description: 'Payment of $2,400 to Tech Corp Ltd', at: hoursAgo(6), status: 'ok' },
  { id: 'se-3', type: 'alert', description: 'New device sign-in attempt blocked (Lagos, NG)', at: daysAgo(2), status: 'warning' },
  { id: 'se-4', type: 'settings', description: 'Two-factor authentication enabled', at: daysAgo(5), status: 'ok' },
];

export const MOCK_LIMITS: TransactionLimit[] = [
  { id: 'l-1', label: 'Per Transaction', period: 'per-transaction', limit: 10000, used: 0, currency: 'USD' },
  { id: 'l-2', label: 'Daily Spending', period: 'daily', limit: 25000, used: 6400, currency: 'USD' },
  { id: 'l-3', label: 'Monthly Spending', period: 'monthly', limit: 100000, used: 38250, currency: 'USD' },
];

export const MOCK_PAYROLL: PayrollRun[] = [
  { id: 'pr-1', period: 'June 2026', employees: 24, total: 86400, currency: 'USD', status: 'scheduled', payDate: daysAhead(5) },
  { id: 'pr-2', period: 'May 2026', employees: 24, total: 85200, currency: 'USD', status: 'paid', payDate: daysAgo(25) },
  { id: 'pr-3', period: 'April 2026', employees: 22, total: 79800, currency: 'USD', status: 'paid', payDate: daysAgo(55) },
];

export const MOCK_SUBSCRIPTIONS: Subscription[] = [
  { id: 'sub-1', name: 'AWS Cloud', vendor: 'Amazon Web Services', amount: 1240, currency: 'USD', cycle: 'monthly', nextCharge: daysAhead(8), status: 'active', category: 'company' },
  { id: 'sub-2', name: 'Slack Enterprise', vendor: 'Salesforce', amount: 480, currency: 'USD', cycle: 'monthly', nextCharge: daysAhead(15), status: 'active', category: 'company' },
  { id: 'sub-3', name: 'Office Lease', vendor: 'WeWork', amount: 5200, currency: 'USD', cycle: 'monthly', nextCharge: daysAhead(2), status: 'active', category: 'company' },
  { id: 'sub-4', name: 'Business Loan', vendor: 'Chase Bank', amount: 2150, currency: 'USD', cycle: 'monthly', nextCharge: daysAhead(11), status: 'active', category: 'debt' },
  { id: 'sub-5', name: 'Equipment Financing', vendor: 'Dell Financial', amount: 890, currency: 'USD', cycle: 'monthly', nextCharge: daysAhead(20), status: 'active', category: 'debt' },
];

/** Time-series helpers for charts. */
export const SPENDING_SERIES = [
  { month: 'Jan', sent: 6400, received: 8200 },
  { month: 'Feb', sent: 7100, received: 7600 },
  { month: 'Mar', sent: 5300, received: 9100 },
  { month: 'Apr', sent: 8200, received: 8800 },
  { month: 'May', sent: 6900, received: 10200 },
  { month: 'Jun', sent: 7400, received: 9600 },
];

export const CATEGORY_BREAKDOWN = [
  { name: 'Services', value: 38, color: '#1A1A1A' },
  { name: 'Subscriptions', value: 24, color: '#6366f1' },
  { name: 'Payroll', value: 20, color: '#10b981' },
  { name: 'International', value: 12, color: '#f59e0b' },
  { name: 'Other', value: 6, color: '#cbd5e1' },
];

export const RATE_HISTORY = Array.from({ length: 12 }).map((_, i) => ({
  t: `${i * 2}:00`,
  rate: 0.9 + Math.sin(i / 2) * 0.03 + Math.random() * 0.01,
}));
